import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { isEqual, isEmpty, merge } from 'lodash-es';

// import constants
import { BASE_URL } from '../../constants/env';

// import types
import type { EventByUser } from '../../types/schedule';
import type { SaveRange } from '../../types/date';

// import utils
import {
  type BackendRoomPayloadWithVersion,
  buildPayloadFromSnapshot,
  computeTopLevelDelta,
  isErrorObject,
  normalizeEventsByUser,
  parseJsonSafe,
} from '../../utils/persistence';
import { logger } from '../../utils/logger';

// import apis
import { getRoom, putRoom, patchRoom } from '../../api/rooms';

// import hooks
import { useLinkedDevices } from '../device/useLinkedDevices';
import { MAX_PARTICIPANTS } from '../../constants/schedule';

export type RoomSnapshot = {
  users: string[]; // userIds
  userIdToName: Record<string, string>;
  ownerByUserId: Record<string, string>; // userId -> deviceId
  eventsByUser: EventByUser; // keyed by userId
  gridStart: string;
  gridEnd: string;
  savedRange?: SaveRange;
  ownerDeviceId?: string;
  ownerTimezone?: string;
  eventTitle?: string;
  maxParticipants?: number;
  payloadVersion?: number;
};

export type UseRoomPersistenceParams = {
  roomId?: string;
  initialTZ: string;
  getSnapshot: () => RoomSnapshot;
  applySnapshot: (snap: RoomSnapshot) => void;
};

// Shape of payload returned by backend API
export type BackendRoomPayload = {
  users?: string[];
  userIdToName?: Record<string, string> | undefined;
  ownerByUserId?: Record<string, string> | undefined;
  eventsByUser?: EventByUser | undefined;
  gridStart?: string;
  gridEnd?: string;
  savedRange?: SaveRange | undefined;
  ownerDeviceId?: string | undefined;
  ownerTimezone?: string | undefined;
  eventTitle?: string | undefined;
  maxParticipants: number;
};

/**
 * Centralizes ownership + persistence (localStorage and optional backend) logic for a room.
 * - Manages deviceId, storage key, ETag, and dirty state.
 * - Loads from backend when configured; falls back to local storage.
 * - Provides save/cancel actions mirroring pre-refactor behavior.
 */
export default function useRoomPersistence(params: UseRoomPersistenceParams) {
  const { roomId, initialTZ, getSnapshot, applySnapshot } = params;

  // Keep the latest callbacks in refs to avoid the effect dependency churn
  const getSnapshotRef = useRef(getSnapshot);
  const applySnapshotRef = useRef(applySnapshot);

  useEffect(() => {
    getSnapshotRef.current = getSnapshot;
  }, [getSnapshot]);

  useEffect(() => {
    applySnapshotRef.current = applySnapshot;
  }, [applySnapshot]);

  // Use linked devices hook to check ownership
  const {
    deviceId,
    linkedDeviceIds,
    isOwnerDevice: checkIsOwnerDevice,
  } = useLinkedDevices();

  // Last known payload (JSON string) and backend ETag
  const lastSavedJson = useRef<string | null>(null);
  const lastEtag = useRef<string | undefined>(undefined);

  const [isDirty, setIsDirty] = useState(false);
  // First-load indicator for initial backend fetch
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  // Track if initial load has completed to prevent showing loading again on subsequent effect runs
  const lastRoomIdRef = useRef<string | undefined>(undefined);
  const hasCompletedInitialLoad = useRef(false);

  // Session-scoped owner logic: capture the owner at first hydration/claim this session
  // and keep the computed isOwner stable for the session. This matches the requirement
  // to "check the owner as the first time when the user opens the website".
  const sessionOwnerRef = useRef<string | undefined>(undefined);
  const [isOwner, setIsOwner] = useState(false);
  const captureSessionOwner = useCallback(
    (ownerId: string | undefined) => {
      if (sessionOwnerRef.current === undefined) {
        sessionOwnerRef.current = ownerId;
        setIsOwner(checkIsOwnerDevice(ownerId));
      }
    },
    [checkIsOwnerDevice],
  );

  // Build persisted payload from a given snapshot (or current), normalizing tz on events
  const buildPayloadFrom = useCallback(
    (snap: RoomSnapshot) => buildPayloadFromSnapshot(snap, initialTZ),
    [initialTZ],
  );

  const buildPayload = useCallback(
    () => buildPayloadFrom(getSnapshotRef.current()),
    [buildPayloadFrom],
  );

  // Re-evaluate session ownership when linked devices change
  useEffect(() => {
    if (sessionOwnerRef.current !== undefined) {
      setIsOwner(checkIsOwnerDevice(sessionOwnerRef.current));
    }
  }, [checkIsOwnerDevice]);

  // Dirty tracker: compare the current snapshot JSON to last saved
  useEffect(() => {
    if (lastSavedJson.current === null) return;
    const json = JSON.stringify(buildPayload());
    setIsDirty(!isEqual(lastSavedJson.current, json));
  }, [buildPayload]);

  // Backend hydration when configured
  useEffect(() => {
    const canRun = !!BASE_URL && !!roomId && !!deviceId;
    if (!canRun) return;

    // Reset the flag if roomId changed (new room = new initial load)
    if (lastRoomIdRef.current !== roomId) {
      hasCompletedInitialLoad.current = false;
      lastRoomIdRef.current = roomId;
    }

    let aborted = false;
    logger.log('HYDRATION - Starting:', roomId, deviceId, aborted);

    // Show first-load indicator only for the very first backend hydration
    // Don't show loading again if initial load has already completed
    if (!hasCompletedInitialLoad.current) {
      setIsInitialLoading(true);
    }

    const hydrate = (payload: BackendRoomPayload) => {
      logger.log('HYDRATION - aborted:', aborted);

      if (aborted) return;
      try {
        const snap: RoomSnapshot = {
          users: Array.isArray(payload.users) ? payload.users : [],
          userIdToName:
            typeof payload.userIdToName === 'object' && payload.userIdToName
              ? payload.userIdToName
              : {},
          ownerByUserId:
            typeof payload.ownerByUserId === 'object' && payload.ownerByUserId
              ? payload.ownerByUserId
              : {},
          eventsByUser:
            typeof payload.eventsByUser === 'object' && payload.eventsByUser
              ? payload.eventsByUser
              : {},
          gridStart: payload.gridStart || getSnapshotRef.current().gridStart,
          gridEnd: payload.gridEnd || getSnapshotRef.current().gridEnd,
          savedRange:
            payload.savedRange &&
            payload.savedRange.fromYmd &&
            payload.savedRange.toYmd
              ? payload.savedRange
              : getSnapshotRef.current().savedRange,
          ownerDeviceId:
            typeof payload.ownerDeviceId === 'string'
              ? payload.ownerDeviceId
              : undefined,
          ownerTimezone:
            typeof payload.ownerTimezone === 'string'
              ? payload.ownerTimezone
              : undefined,
          eventTitle:
            typeof payload.eventTitle === 'string'
              ? payload.eventTitle
              : undefined,
          maxParticipants: payload.maxParticipants ?? MAX_PARTICIPANTS,
        };

        // Normalize incoming backend events too
        snap.eventsByUser = normalizeEventsByUser(snap.eventsByUser);
        applySnapshotRef.current(snap);
        captureSessionOwner(snap.ownerDeviceId);
        return snap;
      } catch (e) {
        logger.error('Error applying backend payload:', e);
      }
    };

    const run = async () => {
      try {
        const { payload, etag } = await getRoom<BackendRoomPayload>(
          roomId,
          deviceId,
        );
        lastEtag.current = etag;
        logger.log('HYDRATION - Room fetched from backend:', payload);

        // Apply backend payload first, then compute the normalized representation from the exact applied snapshot
        let appliedSnap = hydrate(payload);
        logger.log('HYDRATION - Room normalized from backend:', appliedSnap);

        try {
          // If the backend document has no owner yet, let the first visitor claim ownership.
          if (appliedSnap && !appliedSnap.ownerDeviceId) {
            logger.log('HYDRATION - Room has no owner, claiming ownership...');

            // Decide owner's timezone as a concrete string to satisfy RoomSnapshot typing
            const ownerTz: string = appliedSnap.ownerTimezone || initialTZ;
            const delta: Partial<BackendRoomPayload> = {
              ownerDeviceId: deviceId,
              ownerTimezone: ownerTz,
            };
            try {
              const res = await patchRoom(roomId, delta, {
                etag,
                deviceId,
              });
              lastEtag.current = res.etag;
              // Apply claimed owner to local state and recompute normalized payload
              const updatedSnap = {
                ...appliedSnap,
                ownerDeviceId: deviceId,
                ownerTimezone: ownerTz,
              };

              applySnapshotRef.current(updatedSnap);
              captureSessionOwner(updatedSnap.ownerDeviceId);
              appliedSnap = updatedSnap;

              const normalized = JSON.stringify(buildPayloadFrom(updatedSnap));
              lastSavedJson.current = normalized;
              setIsDirty(false);

              logger.log('HYDRATION - Room claimed ownership:', res);
            } catch (claimErr: unknown) {
              logger.error('Owner auto-claim failed:', claimErr);
              // If the claim failed due to ETag mismatch or other race, fetch the latest and settle
              if (isErrorObject(claimErr, 412)) {
                try {
                  const { payload: latest, etag: latestEtag } =
                    await getRoom<BackendRoomPayload>(roomId, deviceId);
                  lastEtag.current = latestEtag;
                  const latestSnap = hydrate(latest);
                  captureSessionOwner(latestSnap?.ownerDeviceId);
                  const normalized = JSON.stringify(
                    latestSnap ? buildPayloadFrom(latestSnap) : buildPayload(),
                  );
                  lastSavedJson.current = normalized;
                  setIsDirty(false);

                  logger.log(
                    'HYDRATION - Room claimed ownership after conflict resolution:',
                    latestSnap,
                  );
                } catch (e) {
                  logger.error('Failed to refresh after claim conflict:', e);
                }
              } else {
                logger.error('Owner auto-claim failed:', claimErr);
                // Fall through to set normalized based on the unclaimed snapshot
                const normalized = JSON.stringify(
                  appliedSnap ? buildPayloadFrom(appliedSnap) : buildPayload(),
                );
                lastSavedJson.current = normalized;
                setIsDirty(false);
              }
            }
          } else {
            const payload = appliedSnap
              ? buildPayloadFrom(appliedSnap)
              : buildPayload();
            const normalized = JSON.stringify(payload);
            lastSavedJson.current = normalized;
            setIsDirty(false);

            logger.log(
              'HYDRATION - Room has owner, finalized normalized:',
              payload,
            );
          }
        } catch (e) {
          logger.error('Failed to normalize backend snapshot:', e);
        }
      } catch (err: unknown) {
        logger.error('Failed to fetch room from backend:', err);
        if (isErrorObject(err, 404)) {
          logger.error('Backend room not found:', roomId);

          // Create new room on backend, claiming ownership if missing
          const cur = getSnapshotRef.current();
          const initSnap: RoomSnapshot = {
            ...cur,
            ownerDeviceId: cur.ownerDeviceId || deviceId,
            ownerTimezone: cur.ownerTimezone || initialTZ,
          };

          logger.log('HYDRATION - Creating new room on backend:', initSnap);
          applySnapshotRef.current(initSnap);
          captureSessionOwner(initSnap.ownerDeviceId);

          const payload = buildPayloadFrom(initSnap);
          const json = JSON.stringify(payload);

          try {
            const res = await putRoom(roomId, payload, {
              deviceId,
            });
            lastEtag.current = res.etag;
            lastSavedJson.current = json;
            setIsDirty(false);

            logger.log('HYDRATION - Room created on backend:', res);
          } catch (e) {
            logger.error('Failed to create room on backend:', e);
          }
        } else {
          logger.error('Backend unavailable when hydrating room:', err);
        }
      } finally {
        if (!aborted) {
          setIsInitialLoading(false);
          hasCompletedInitialLoad.current = true;
        }
      }
    };
    run();

    return () => {
      logger.log('HYDRATION - aborted:', aborted);
      aborted = true;
    };
  }, [
    roomId,
    deviceId,
    buildPayload,
    initialTZ,
    captureSessionOwner,
    buildPayloadFrom,
  ]);

  // Exposed actions
  const notifyChanged = useCallback(() => {
    try {
      if (lastSavedJson.current === null) return;
      const json = JSON.stringify(buildPayload());
      setIsDirty(!isEqual(lastSavedJson.current, json));
    } catch (e: unknown) {
      logger.error('Failed to notify change:', e);
    }
  }, [buildPayload]);

  const saveNow = useCallback(
    async (_payload?: RoomSnapshot) => {
      const payload = _payload ?? buildPayload();
      logger.log('Saving room payload:', payload);

      // Use to show toast on success or failure
      const toastId = toast.loading('Saving changes...');
      let isSuccess = false;

      // Helper to parse last saved base
      const lastSavedBase = parseJsonSafe<
        Partial<BackendRoomPayloadWithVersion>
      >(lastSavedJson.current);

      // Compute minimal top-level delta
      const delta = computeTopLevelDelta(lastSavedBase ?? null, payload);
      logger.log('Top-level delta:', delta);

      // Abort if there's nothing to send'
      if (isEmpty(delta)) {
        setIsDirty(false);
        return;
      }

      // Ownership-aware filtering: only send changes for users owned by this device (including linked devices).
      const owners = (payload.ownerByUserId || {}) as Record<string, string>;
      const ownedUserIds = new Set(
        Object.keys(owners).filter((u) => linkedDeviceIds.includes(owners[u])),
      );

      const filtered: Partial<BackendRoomPayloadWithVersion> = {};

      // Claim new users (ownerByUserId) only when claiming to self and was absent before
      if (delta.ownerByUserId) {
        const baseOwners = (lastSavedBase?.ownerByUserId || {}) as Record<
          string,
          string
        >;
        const nextOwners = (payload.ownerByUserId || {}) as Record<
          string,
          string
        >;
        const claims: Record<string, string> = {};
        Object.keys(nextOwners).forEach((uid) => {
          if (baseOwners[uid] == null && nextOwners[uid] === deviceId) {
            claims[uid] = deviceId;
          }
        });
        if (Object.keys(claims).length) filtered.ownerByUserId = claims;
      }

      // userIdToName: include owned additions/updates and owned removals
      if (delta.userIdToName) {
        const baseU2N = (lastSavedBase?.userIdToName || {}) as Record<
          string,
          string
        >;
        const nextU2N = (payload.userIdToName || {}) as Record<string, string>;
        // IMPORTANT: for removals, ownership must be checked against the previous owners snapshot
        const baseOwners = (lastSavedBase?.ownerByUserId || {}) as Record<
          string,
          string
        >;
        const out: Record<string, string | null> = {};
        // additions/updates
        Object.keys(nextU2N).forEach((uid) => {
          if (ownedUserIds.has(uid) && !isEqual(baseU2N[uid], nextU2N[uid])) {
            out[uid] = nextU2N[uid];
          }
        });
        // removals
        Object.keys(baseU2N).forEach((uid) => {
          if (
            !(uid in nextU2N) &&
            linkedDeviceIds.includes(baseOwners[uid]) // check ownership from base
          ) {
            // Use null to request server-side deletion of this key
            out[uid] = null;
          }
        });
        if (Object.keys(out).length)
          // cast: API accepts null per-key for deletion even if local type is string
          filtered.userIdToName = out as Record<string, string>;
      }

      // eventsByUser: include owned additions/updates and owned removals
      if (delta.eventsByUser) {
        const baseEv = (lastSavedBase?.eventsByUser || {}) as EventByUser;
        const nextEv = (payload.eventsByUser || {}) as EventByUser;
        // For removals, check ownership against base owners
        const baseOwners = (lastSavedBase?.ownerByUserId || {}) as Record<
          string,
          string
        >;
        const out: EventByUser = {};
        // additions/updates
        Object.keys(nextEv).forEach((uid) => {
          if (ownedUserIds.has(uid) && !isEqual(baseEv[uid], nextEv[uid])) {
            out[uid] = nextEv[uid];
          }
        });
        // removals: send an empty array to wipe events for owned userIds removed from a map
        Object.keys(baseEv).forEach((uid) => {
          if (!(uid in nextEv) && linkedDeviceIds.includes(baseOwners[uid])) {
            out[uid] = [];
          }
        });
        if (Object.keys(out).length) filtered.eventsByUser = out;
      }

      // users: include only if adds/removes involve owned userIds; send a full desired array
      if (delta.users) {
        const baseUsers = Array.isArray(lastSavedBase?.users)
          ? lastSavedBase!.users
          : [];
        const nextUsers = Array.isArray(payload.users) ? payload.users : [];
        const baseSet = new Set(baseUsers);
        const nextSet = new Set(nextUsers);
        // IMPORTANT: for removals, the removed userId won't be present in "payload.ownerByUserId" anymore,
        // so ownership must be checked against the PREVIOUS snapshot's owners.
        const baseOwners = (lastSavedBase?.ownerByUserId || {}) as Record<
          string,
          string
        >;
        let includeUsers = false;
        // additions
        nextUsers.forEach((uid) => {
          if (!baseSet.has(uid) && ownedUserIds.has(uid)) includeUsers = true;
        });
        // removals
        const removedOwnedUids: string[] = [];
        baseUsers.forEach((uid) => {
          // Check ownership using the base owners map, because the user no longer exists in "next"
          if (!nextSet.has(uid) && linkedDeviceIds.includes(baseOwners[uid])) {
            includeUsers = true;
            removedOwnedUids.push(uid);
          }
        });
        if (includeUsers) filtered.users = nextUsers;

        // Also remove unused ids from ownerByUserId when owned users are removed from the room
        if (removedOwnedUids.length) {
          const existing = (filtered.ownerByUserId || {}) as Record<
            string,
            string | null
          >;
          removedOwnedUids.forEach((uid) => {
            existing[uid] = null;
          });
          filtered.ownerByUserId = existing as Record<string, string>;
        }
      }

      // Global fields: allowed only for a room owner (including linked devices)
      const canApplyGlobalFields =
        isOwner ||
        checkIsOwnerDevice(payload.ownerDeviceId) ||
        checkIsOwnerDevice(lastSavedBase?.ownerDeviceId) ||
        checkIsOwnerDevice(sessionOwnerRef.current);
      logger.log('Global fields allowed:', canApplyGlobalFields);

      const maybeAddGlobal = <K extends keyof BackendRoomPayload>(key: K) => {
        if (delta[key] !== undefined && canApplyGlobalFields) {
          filtered[key] = payload[key] as BackendRoomPayload[K];
        }
      };
      maybeAddGlobal('eventTitle');
      maybeAddGlobal('gridStart');
      maybeAddGlobal('gridEnd');
      maybeAddGlobal('savedRange');
      maybeAddGlobal('ownerTimezone');
      maybeAddGlobal('ownerDeviceId');

      // Always propagate the payload Version when present in delta
      if (delta.payloadVersion !== undefined) {
        filtered.payloadVersion = payload.payloadVersion;
      }

      logger.log(
        "Abort if there's nothing to send:",
        BASE_URL,
        roomId,
        filtered,
      );

      // Abort if there's nothing to send
      if (!BASE_URL || !roomId) return;
      if (Object.keys(filtered).length === 0) {
        // Nothing we are allowed to update; do not clear dirty here
        return;
      }

      // Remove users from the lastSavedBase that are not present in users of filtered
      if (filtered.users && lastSavedBase) {
        const filteredUserSet = new Set(filtered.users);

        // ownerByUserId: mark missing userIds as null (relinquish)
        if (
          lastSavedBase.ownerByUserId &&
          typeof lastSavedBase.ownerByUserId === 'object'
        ) {
          const owners = lastSavedBase.ownerByUserId as Record<
            string,
            string | null
          >;
          Object.keys(owners).forEach((uid) => {
            if (!filteredUserSet.has(uid)) owners[uid] = null;
          });
        }

        // userIdToName: mark missing userIds as null (delete key)
        if (
          lastSavedBase.userIdToName &&
          typeof lastSavedBase.userIdToName === 'object'
        ) {
          const u2n = lastSavedBase.userIdToName as Record<
            string,
            string | null
          >;
          Object.keys(u2n).forEach((uid) => {
            if (!filteredUserSet.has(uid)) u2n[uid] = null;
          });
        }

        // eventsByUser: mark missing userIds as empty array (wipe)
        if (
          lastSavedBase.eventsByUser &&
          typeof lastSavedBase.eventsByUser === 'object'
        ) {
          const ev = lastSavedBase.eventsByUser as Record<string, unknown[]>;
          Object.keys(ev).forEach((uid) => {
            if (!filteredUserSet.has(uid)) ev[uid] = [] as unknown[];
          });
        }

        // users: replace with filtered list
        if (Array.isArray(lastSavedBase.users)) {
          lastSavedBase.users = filtered.users;
        }
      }

      // Optimistic update: assume save will succeed and immediately reflect it in UI
      // by marking the state as not dirty and updating lastSavedJson to the expected
      // merged result. If the request fails, we'll roll this back.
      const prevSavedJson = lastSavedJson.current;
      // Only merge what we actually send; keep disallowed fields dirty for retry
      const optimisticMerged: Partial<BackendRoomPayloadWithVersion> = merge(
        lastSavedBase,
        filtered,
      );

      logger.log('Sending delta:', delta);
      logger.log('Sending filtered:', filtered);
      logger.log('Optimistically merged base:', lastSavedBase);
      logger.log('Optimistically merged:', optimisticMerged);
      lastSavedJson.current = JSON.stringify(optimisticMerged);
      setIsDirty(false);

      try {
        // Primary path: PATCH only filtered fields with ETag
        const res = await patchRoom(roomId, filtered, {
          etag: lastEtag.current,
          deviceId: deviceId,
        });
        lastEtag.current = res.etag;
        isSuccess = true;
        logger.log('PATCH succeeded:', res);
        return;
      } catch (err: unknown) {
        if (isErrorObject(err, 412)) {
          // ETag conflict: fetch the latest, re-apply filtered over latest
          logger.error(
            'Failed to update via PATCH; attempting to resolve conflict:',
            err,
          );
          try {
            // Fetch the latest snapshot and apply filtered over it
            const res1 = await getRoom<BackendRoomPayload>(roomId, deviceId);
            const { payload: latest, etag: latestEtag } = res1;

            // Re-apply filtered over the latest and retry PATCH
            const res2 = await patchRoom(roomId, filtered, {
              etag: latestEtag,
              deviceId: deviceId,
            });
            lastEtag.current = res2.etag;

            // Merge the result of the PATCH into the optimistic state
            const merged: Partial<BackendRoomPayloadWithVersion> = merge(
              latest,
              filtered,
            );

            lastSavedJson.current = JSON.stringify(merged);
            setIsDirty(false);
            isSuccess = true;
            logger.log('SAVE NOW - PATCH resolved conflict:', res2);

            return;
          } catch (e) {
            logger.error('Failed to resolve conflict via PATCH:', e);
            // Rollback optimistic state on failure to resolve conflict
            lastSavedJson.current = prevSavedJson;
            setIsDirty(true);
            return;
          }
        }

        // Fallback: full PUT only if PATCH is not acceptable (backend will also gate)
        logger.error(
          'Failed to update room via PATCH; falling back to PUT:',
          err,
        );

        try {
          const res = await putRoom(roomId, payload, {
            etag: lastEtag.current,
            deviceId: deviceId,
          });
          lastEtag.current = res.etag;
          lastSavedJson.current = JSON.stringify(payload);
          setIsDirty(false);
          isSuccess = true;
          logger.log('SAVE NOW - PUT succeeded:', res);

          return;
        } catch (e) {
          logger.error('PUT fallback failed:', e);
          // Rollback optimistic state when PUT also fails
          lastSavedJson.current = prevSavedJson;
          setIsDirty(true);
          return;
        }
      } finally {
        if (isSuccess) {
          toast.success('Changes saved', { id: toastId });
        } else {
          toast.error('Failed to save changes', { id: toastId });
        }
      }
    },
    [
      roomId,
      buildPayload,
      deviceId,
      linkedDeviceIds,
      isOwner,
      checkIsOwnerDevice,
    ],
  );

  const cancelChanges = useCallback(() => {
    const snapshotJson = lastSavedJson.current;
    if (!snapshotJson) return;
    try {
      const data = JSON.parse(snapshotJson);
      logger.log('CANCEL CHANGES - Snapshot:', data);

      const snap: RoomSnapshot = {
        users: Array.isArray(data.users) ? data.users : [],
        userIdToName:
          typeof data.userIdToName === 'object' && data.userIdToName
            ? data.userIdToName
            : {},
        ownerByUserId:
          typeof data.ownerByUserId === 'object' && data.ownerByUserId
            ? data.ownerByUserId
            : {},
        eventsByUser:
          typeof data.eventsByUser === 'object' && data.eventsByUser
            ? data.eventsByUser
            : {},
        gridStart: data.gridStart || getSnapshot().gridStart,
        gridEnd: data.gridEnd || getSnapshot().gridEnd,
        savedRange:
          data.savedRange && data.savedRange.fromYmd && data.savedRange.toYmd
            ? data.savedRange
            : undefined,
        ownerDeviceId:
          typeof data.ownerDeviceId === 'string'
            ? data.ownerDeviceId
            : undefined,
        ownerTimezone:
          typeof data.ownerTimezone === 'string'
            ? data.ownerTimezone
            : undefined,
        eventTitle:
          typeof data.eventTitle === 'string' ? data.eventTitle : undefined,
        maxParticipants: data.maxParticipants ?? MAX_PARTICIPANTS,
      };
      logger.log('CANCEL CHANGES - Normalized snapshot:', snap);

      applySnapshot(snap);
      // Align the lastSavedJson with the normalized payload derived from the snapshot
      try {
        const payload = buildPayloadFrom(snap);
        const normalized = JSON.stringify(payload);
        lastSavedJson.current = normalized;
        logger.log('CANCEL CHANGES - Normalized payload:', payload);
        toast.success('Changes reverted');
      } catch (e) {
        logger.error('Failed to normalize payload for storage:', e);
        toast.error('Failed to revert changes');
      }
      setIsDirty(false);
    } catch (e) {
      logger.error('Error cancelling changes from storage:', e);
    }
  }, [getSnapshot, applySnapshot, buildPayloadFrom]);

  return {
    deviceId,
    isOwner,
    isDirty,
    notifyChanged,
    saveNow,
    cancelChanges,
    isInitialLoading,
  };
}
